/**
 * The field catalogue — slice 67, "every column is a field".
 *
 * ## THE OWNER'S BRIEF, AND WHAT IT RULES OUT
 *
 * "It's not only about dob/date_of_birth, it can be any custom column user wants
 * to add to the rule. It should not be fixed." So no list in this file names a
 * column a customer might upload. `GET /fields` answers with:
 *
 * - **Profile** — `name`, `email`, `phone`, `first_seen`, `source`, `dataset`:
 *   what Wend itself knows about every person.
 * - **Your columns** — EVERY column of every ready upload, keyed
 *   `extras.<column name verbatim>` ({@link extrasFieldKey}), typed by the
 *   upload's detector unless a person corrected it. When two uploads disagree,
 *   the most recent upload's type wins and `conflict: true` says so.
 * - **Derived** — fields a person defined from another one (`derived:<key>`):
 *   Age from whichever date column holds a birth date, bands, month, weekday.
 * - **Activity** — from data Wend owns: messages sent in 30 days, last messaged,
 *   replied on WhatsApp, clicked a link, converted, each declared outcome type
 *   (`outcome:<id>`), pressure band. A person's being in a flow is deliberately
 *   NOT a field: a flow whose audience is "people in this flow" is a loop.
 *
 * The catalogue is computed on read. Only a person's corrections
 * (`field_settings`) and definitions (`derived_fields`) are stored — see
 * `0051_fields_and_rule_segments.sql` for why a stored catalogue was rejected.
 *
 * ## A PERSON'S VALUE: LATEST UPLOAD WINS
 *
 * A person can have several contact rows, one per upload. Their value for a
 * column is the one on their most recent contact row where that key is
 * non-empty (`created_at desc, id desc`). {@link LATEST_UPLOAD_WINS} is the
 * sentence the API header and the field picker's tooltip both show.
 *
 * ## DATES ARE NEVER GUESSED
 *
 * 03/04/2026 is two different days depending on who exported the sheet. A date
 * column from CSV text whose samples are ambiguous comes back with
 * `needsDateFormat: true` and is usable in a rule only after somebody picks
 * `dmy` or `mdy` (`PUT /fields/:key/settings`). ISO values — and XLSX dates
 * arrive as ISO — need no answer.
 *
 * ## MASKING
 *
 * Sample values never show an email or a phone in full: {@link maskEmail} and
 * {@link maskPhone} are the one way both the API and the screens hide them, so a
 * sample of an `email`-typed column reads `a***@domain.in`.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { columnTypeSchema } from './dataset.js';
import { fieldKeySchema, fieldTypeSchema, type FieldType } from './rules.js';

/* -------------------------------------------------------------------------- */
/* Keys                                                                        */
/* -------------------------------------------------------------------------- */

export const EXTRAS_FIELD_PREFIX = 'extras.';
/** A derived field's slug; the same pattern `derived_fields_shape` enforces. */
const DERIVED_FIELD_KEY_RE = /^[a-z0-9_]{1,40}$/;
export const DERIVED_FIELD_PREFIX = 'derived:';
export const OUTCOME_FIELD_PREFIX = 'outcome:';

/** The catalogue key of an uploaded column: its name verbatim, prefixed. */
export function extrasFieldKey(column: string): string {
  return `${EXTRAS_FIELD_PREFIX}${column}`;
}
export function derivedFieldKey(key: string): string {
  return `${DERIVED_FIELD_PREFIX}${key}`;
}
export function outcomeFieldKey(outcomeTypeId: string): string {
  return `${OUTCOME_FIELD_PREFIX}${outcomeTypeId}`;
}

export const BUILTIN_FIELD_KEY_VALUES = ['name', 'email', 'phone', 'first_seen', 'source', 'dataset'] as const;
export type BuiltinFieldKey = (typeof BUILTIN_FIELD_KEY_VALUES)[number];

export const ACTIVITY_FIELD_KEY_VALUES = [
  'messages_sent_30d',
  'last_messaged_at',
  'replied_whatsapp',
  'clicked_link',
  'converted',
  'pressure_band',
] as const;
export type ActivityFieldKey = (typeof ACTIVITY_FIELD_KEY_VALUES)[number];

/** Built-in and activity fields: their type and label are Wend's, not detected. */
export const FIXED_FIELDS: Readonly<Record<BuiltinFieldKey | ActivityFieldKey, { label: string; type: FieldType }>> = {
  name: { label: 'Name', type: 'text' },
  email: { label: 'Email', type: 'text' },
  phone: { label: 'Phone', type: 'text' },
  first_seen: { label: 'First seen', type: 'date' },
  source: { label: 'Source', type: 'enum' },
  dataset: { label: 'Upload', type: 'enum' },
  messages_sent_30d: { label: 'Messages sent, last 30 days', type: 'number' },
  last_messaged_at: { label: 'Last messaged', type: 'date' },
  replied_whatsapp: { label: 'Replied on WhatsApp', type: 'event' },
  clicked_link: { label: 'Clicked a link', type: 'event' },
  converted: { label: 'Converted', type: 'event' },
  pressure_band: { label: 'Message pressure', type: 'enum' },
};

/** `source` for a person whose earliest row is a Reach lead. */
export const REACH_LEAD_SOURCE = 'reach_lead';

export const PRESSURE_BAND_FIELD_VALUES = ['healthy', 'warning', 'critical'] as const;

export type ParsedFieldKey =
  | { kind: 'builtin'; key: BuiltinFieldKey }
  | { kind: 'activity'; key: ActivityFieldKey }
  | { kind: 'column'; column: string }
  | { kind: 'derived'; key: string }
  | { kind: 'outcome'; outcomeTypeId: string };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * What a key names, by shape. `null` for a key no namespace owns. This says
 * nothing about whether the workspace HAS that column — that is the catalogue.
 */
export function parseFieldKey(key: string): ParsedFieldKey | null {
  if ((BUILTIN_FIELD_KEY_VALUES as readonly string[]).includes(key)) return { kind: 'builtin', key: key as BuiltinFieldKey };
  if ((ACTIVITY_FIELD_KEY_VALUES as readonly string[]).includes(key)) return { kind: 'activity', key: key as ActivityFieldKey };
  if (key.startsWith(EXTRAS_FIELD_PREFIX) && key.length > EXTRAS_FIELD_PREFIX.length) {
    return { kind: 'column', column: key.slice(EXTRAS_FIELD_PREFIX.length) };
  }
  if (key.startsWith(DERIVED_FIELD_PREFIX)) {
    const slug = key.slice(DERIVED_FIELD_PREFIX.length);
    return DERIVED_FIELD_KEY_RE.test(slug) ? { kind: 'derived', key: slug } : null;
  }
  if (key.startsWith(OUTCOME_FIELD_PREFIX)) {
    const id = key.slice(OUTCOME_FIELD_PREFIX.length);
    return UUID_RE.test(id) ? { kind: 'outcome', outcomeTypeId: id.toLowerCase() } : null;
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Settings — a person's correction                                            */
/* -------------------------------------------------------------------------- */

/** The types a person may set a column to. `event` is Wend's, never a column's. */
export const STORED_FIELD_TYPE_VALUES = ['text', 'number', 'boolean', 'date', 'enum'] as const;
export const storedFieldTypeSchema = z.enum(STORED_FIELD_TYPE_VALUES);
export type StoredFieldType = z.infer<typeof storedFieldTypeSchema>;

export const FIELD_DATE_FORMAT_VALUES = ['iso', 'dmy', 'mdy'] as const;
export const fieldDateFormatSchema = z.enum(FIELD_DATE_FORMAT_VALUES);
export type FieldDateFormat = z.infer<typeof fieldDateFormatSchema>;

export const FIELD_DATE_FORMAT_LABEL: Readonly<Record<FieldDateFormat, string>> = {
  iso: 'Year first (2026-04-03)',
  dmy: 'Day first (03/04/2026 is 3 April)',
  mdy: 'Month first (03/04/2026 is 4 March)',
};

export const FIELD_LABEL_MAX = 120;

export const fieldSettingsSchema = z
  .object({
    label: z.string().nullable(),
    type: storedFieldTypeSchema.nullable(),
    dateFormat: fieldDateFormatSchema.nullable(),
    hidden: z.boolean(),
  })
  .strict();
export type FieldSettings = z.infer<typeof fieldSettingsSchema>;

/**
 * `PUT /fields/:key/settings`. Any subset; `null` clears a correction back to
 * what the data says. A blank label clears it too.
 */
export const updateFieldSettingsRequestSchema = z
  .object({
    label: z
      .string()
      .trim()
      .max(FIELD_LABEL_MAX)
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .optional(),
    type: storedFieldTypeSchema.nullable().optional(),
    dateFormat: fieldDateFormatSchema.nullable().optional(),
    hidden: z.boolean().optional(),
  })
  .strict()
  .refine(
    (body) => body.label !== undefined || body.type !== undefined || body.dateFormat !== undefined || body.hidden !== undefined,
    'Nothing to update: send a label, a type, a date format or hidden',
  );
export type UpdateFieldSettingsRequest = z.infer<typeof updateFieldSettingsRequestSchema>;

/** `:key` — URL-encoded by the client (`encodeURIComponent`), decoded by the router. */
export const fieldKeyParamsSchema = z.object({ key: fieldKeySchema }).strict();
export type FieldKeyParams = z.infer<typeof fieldKeyParamsSchema>;

/* -------------------------------------------------------------------------- */
/* Derived fields                                                              */
/* -------------------------------------------------------------------------- */

export const DERIVED_FIELD_KIND_VALUES = ['years_since', 'days_since', 'month_of', 'weekday_of', 'number_band'] as const;
export const derivedFieldKindSchema = z.enum(DERIVED_FIELD_KIND_VALUES);
export type DerivedFieldKind = z.infer<typeof derivedFieldKindSchema>;

/** What each kind produces — the derived field's type in the catalogue. */
export const DERIVED_FIELD_OUTPUT_TYPE: Readonly<Record<DerivedFieldKind, FieldType>> = {
  years_since: 'number',
  days_since: 'number',
  month_of: 'enum',
  weekday_of: 'enum',
  number_band: 'enum',
};

/** What each kind reads. A band reads a number; the rest read a date. */
export const DERIVED_FIELD_SOURCE_TYPE: Readonly<Record<DerivedFieldKind, FieldType>> = {
  years_since: 'date',
  days_since: 'date',
  month_of: 'date',
  weekday_of: 'date',
  number_band: 'number',
};

export const DERIVED_FIELD_KIND_LABEL: Readonly<Record<DerivedFieldKind, string>> = {
  years_since: 'Years since (e.g. Age from a birth date)',
  days_since: 'Days since',
  month_of: 'Month of',
  weekday_of: 'Weekday of',
  number_band: 'Bands of a number',
};

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;
export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export const derivedFieldSlugSchema = z.string().regex(DERIVED_FIELD_KEY_RE, 'Use 1–40 lower-case letters, digits or _');

/**
 * `[[18, 24], [25, 34], …]` — inclusive, ascending, not overlapping. A person
 * whose value falls in no band has no value (unknown), never a made-up band.
 */
export const numberBandsSchema = z
  .array(z.tuple([z.number().finite(), z.number().finite()]))
  .min(1)
  .max(12)
  .superRefine((bands, ctx) => {
    bands.forEach(([low, high], index) => {
      if (low > high) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index], message: 'A band’s low end must not exceed its high end' });
      const previous = bands[index - 1];
      if (previous && low <= previous[1]) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index], message: 'Bands must be in order and must not overlap' });
      }
    });
  });

/** The label of a band, as a catalogue enum value: "18–24". */
export function bandLabel([low, high]: readonly [number, number]): string {
  return `${low.toLocaleString('en-IN')}–${high.toLocaleString('en-IN')}`;
}

export const derivedFieldParamsSchema = z.union([
  z.object({ bands: numberBandsSchema }).strict(),
  z.object({}).strict(),
]);
export type DerivedFieldParams = { bands?: [number, number][] };

const derivedBase = {
  label: z.string().trim().min(1).max(FIELD_LABEL_MAX),
  /** Any catalogue key whose type matches {@link DERIVED_FIELD_SOURCE_TYPE}. */
  sourceField: fieldKeySchema,
  /** Optional: derived from the label when omitted ("Age" → `age`). */
  key: derivedFieldSlugSchema.optional(),
};

/** `POST /fields/derived`. */
export const createDerivedFieldRequestSchema = z.discriminatedUnion('kind', [
  z.object({ ...derivedBase, kind: z.literal('years_since'), params: z.object({}).strict().default({}) }).strict(),
  z.object({ ...derivedBase, kind: z.literal('days_since'), params: z.object({}).strict().default({}) }).strict(),
  z.object({ ...derivedBase, kind: z.literal('month_of'), params: z.object({}).strict().default({}) }).strict(),
  z.object({ ...derivedBase, kind: z.literal('weekday_of'), params: z.object({}).strict().default({}) }).strict(),
  z.object({ ...derivedBase, kind: z.literal('number_band'), params: z.object({ bands: numberBandsSchema }).strict() }).strict(),
]);
export type CreateDerivedFieldRequest = z.infer<typeof createDerivedFieldRequestSchema>;

/** A slug from a label: "Age (years)" → `age_years`. Empty input gives `field`. */
export function slugifyFieldLabel(label: string): string {
  const slug = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return slug === '' ? 'field' : slug;
}

export const derivedFieldSchema = z
  .object({
    id: uuidSchema,
    /** The slug. */
    key: derivedFieldSlugSchema,
    /** `derived:<key>` — what rules use. */
    fieldKey: fieldKeySchema,
    label: z.string(),
    kind: derivedFieldKindSchema,
    sourceField: fieldKeySchema,
    params: z.object({ bands: z.array(z.tuple([z.number(), z.number()])).optional() }).strict(),
    outputType: fieldTypeSchema,
    createdAt: isoDateTimeSchema,
  })
  .strict();
export type DerivedField = z.infer<typeof derivedFieldSchema>;

export const derivedFieldListResponseSchema = z.object({ derivedFields: z.array(derivedFieldSchema) }).strict();
export type DerivedFieldListResponse = z.infer<typeof derivedFieldListResponseSchema>;

export const derivedFieldResponseSchema = z.object({ derivedField: derivedFieldSchema }).strict();
export type DerivedFieldResponse = z.infer<typeof derivedFieldResponseSchema>;

export const derivedFieldParamsRouteSchema = z.object({ derivedFieldId: uuidSchema }).strict();
export type DerivedFieldRouteParams = z.infer<typeof derivedFieldParamsRouteSchema>;

/**
 * The 409 body of `DELETE /fields/derived/:id` when something still reads it:
 * a rule segment's rules or a live (or paused) flow's graph. The users are named
 * so the person can go and change them — "in use" with no list is a dead end.
 */
export const derivedFieldInUseSchema = z
  .object({
    message: z.string().min(1),
    users: z
      .array(z.object({ kind: z.enum(['segment', 'flow']), id: uuidSchema, name: z.string() }).strict())
      .min(1),
  })
  .strict();
export type DerivedFieldInUse = z.infer<typeof derivedFieldInUseSchema>;

/* -------------------------------------------------------------------------- */
/* The catalogue                                                               */
/* -------------------------------------------------------------------------- */

export const FIELD_GROUP_VALUES = ['profile', 'columns', 'derived', 'activity'] as const;
export const fieldGroupSchema = z.enum(FIELD_GROUP_VALUES);
export type FieldGroup = z.infer<typeof fieldGroupSchema>;

export const FIELD_GROUP_LABEL: Readonly<Record<FieldGroup, string>> = {
  profile: 'Profile',
  columns: 'Your columns',
  derived: 'Derived',
  activity: 'Activity',
};

/** Stated in the API header and the picker's tooltip. */
export const LATEST_UPLOAD_WINS =
  'Latest upload wins: when a person appears in several uploads, their value is the one from the most recent upload that has it.';

export const FIELD_SAMPLE_LIMIT = 5;
export const FIELD_ENUM_VALUE_LIMIT = 100;

/** Where a field comes from — discriminated, so a column always says which. */
export const fieldOriginSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('builtin') }).strict(),
  z
    .object({
      kind: z.literal('column'),
      /** The column name verbatim — `extras` key. */
      column: z.string().min(1),
      /** Ready uploads carrying this column. */
      datasetCount: z.number().int().min(1),
      /** The most recent upload's detected type. */
      detectedType: columnTypeSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('derived'),
      derivedFieldId: uuidSchema,
      derivedKind: derivedFieldKindSchema,
      sourceField: fieldKeySchema,
    })
    .strict(),
  z.object({ kind: z.literal('activity') }).strict(),
]);
export type FieldOrigin = z.infer<typeof fieldOriginSchema>;

export const fieldSchema = z
  .object({
    key: fieldKeySchema,
    /** The person's label when they set one, else Wend's or the column name. */
    label: z.string().min(1),
    group: fieldGroupSchema,
    type: fieldTypeSchema,
    origin: fieldOriginSchema,
    /**
     * Share of people with a non-empty value, 0–1. `null` when the workspace has
     * no people — not 0, which would claim a measurement over nobody.
     */
    coverage: z.number().min(0).max(1).nullable(),
    /** Up to five example values, masked for email and phone columns. */
    samples: z.array(z.string()).max(FIELD_SAMPLE_LIMIT),
    /** The options an `enum` field offers (dataset names, months, bands…). Null for other types. */
    values: z.array(z.string()).max(FIELD_ENUM_VALUE_LIMIT).nullable(),
    /** Uploads disagree about this column's type; the most recent upload's won. */
    conflict: z.boolean(),
    /** A CSV date column with ambiguous samples and no format chosen. Rules refuse it until one is. */
    needsDateFormat: z.boolean(),
    /** The format in force: the person's choice, or `iso` when every sample is ISO; null when unknown. */
    dateFormat: fieldDateFormatSchema.nullable(),
    hidden: z.boolean(),
    /** False when hidden or when `needsDateFormat`. */
    usableInRules: z.boolean(),
    /** The stored correction, if any. Null when the data is speaking for itself. */
    settings: fieldSettingsSchema.nullable(),
  })
  .strict();
export type Field = z.infer<typeof fieldSchema>;

/**
 * "Age from <column>" — offered, never created. Made when a date column's
 * normalised name contains `dob`, `birth` or `born`, and not already derived.
 */
export const derivedFieldSuggestionSchema = z
  .object({
    kind: derivedFieldKindSchema,
    sourceField: fieldKeySchema,
    label: z.string().min(1),
    key: derivedFieldSlugSchema,
    /** One sentence: why this is offered. */
    reason: z.string().min(1),
  })
  .strict();
export type DerivedFieldSuggestion = z.infer<typeof derivedFieldSuggestionSchema>;

/** Name fragments that suggest a birth date — matched on a lower-cased name with separators removed. */
export const BIRTH_DATE_NAME_HINTS = ['dob', 'birth', 'born'] as const;

/** True when a column name looks like a birth date ("Date of Birth", "dob", "Born on"). */
export function looksLikeBirthDate(column: string): boolean {
  const normalised = column.toLowerCase().replace(/[^a-z]/g, '');
  return BIRTH_DATE_NAME_HINTS.some((hint) => normalised.includes(hint));
}

/** `GET /fields`. */
export const fieldCatalogueResponseSchema = z
  .object({
    fields: z.array(fieldSchema),
    suggestions: z.array(derivedFieldSuggestionSchema),
    /** People in the workspace — the denominator of every coverage. */
    people: z.number().int().min(0),
    generatedAt: isoDateTimeSchema,
  })
  .strict();
export type FieldCatalogueResponse = z.infer<typeof fieldCatalogueResponseSchema>;

export const fieldResponseSchema = z.object({ field: fieldSchema }).strict();
export type FieldResponse = z.infer<typeof fieldResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Masking                                                                     */
/* -------------------------------------------------------------------------- */

/** `asha@bank.co.in` → `a***@bank.co.in`. Anything not email-shaped → `•••`. */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return '•••';
  return `${email.charAt(0)}***${email.slice(at)}`;
}

/** `+919876512123` → `+91 ••••• ••123`. Only the country code and last three digits survive. */
export function maskPhone(phone: string): string {
  const digits = phone.replace(/[^0-9]/g, '');
  if (digits.length < 6) return '•••';
  const country = phone.startsWith('+91') ? '+91' : `+${digits.slice(0, Math.max(1, digits.length - 10))}`;
  return `${country} ••••• ••${digits.slice(-3)}`;
}
